import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useGetSession, getGetSessionQueryKey } from "@workspace/api-client-react";
import { clearPendingInput, UNFOLD_FAILED_PARAM } from "@/lib/pending-input";

const MESSAGES = [
  { text: "Reading the post…", blobColor: "hsl(258 45% 75%)" },
  { text: "Pulling out the key ideas…", blobColor: "hsl(355 70% 80%)" },
  { text: "Finding what matters to you…", blobColor: "hsl(160 30% 72%)" },
  { text: "Crafting your reflection questions…", blobColor: "hsl(42 75% 76%)" },
  { text: "Almost ready to unfold…", blobColor: "hsl(210 55% 78%)" },
];

export default function ProcessingScreen() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const [msgIndex, setMsgIndex] = useState(0);

  const sessionId = params.sessionId!;
  const { data: session } = useGetSession(sessionId, {
    query: { queryKey: getGetSessionQueryKey(sessionId), refetchInterval: 2000 },
  });

  useEffect(() => {
    if (!session) return;
    if (session.status === "needs_answer") {
      clearPendingInput();
      setLocation(`/reflect/${session.id}`);
    } else if (session.status === "complete" && session.journalId) {
      clearPendingInput();
      setLocation(`/journal/${session.journalId}`);
    } else if (session.status === "error") {
      // Back to the input with what they entered (kept by the input screen) and an explanation.
      setLocation(`/app?${UNFOLD_FAILED_PARAM}=1`, { replace: true });
    }
  }, [session, setLocation]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex((p) => (p + 1) % MESSAGES.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const msg = MESSAGES[msgIndex];

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center relative overflow-hidden">

      {/* Slow-moving background wash */}
      <motion.div
        animate={{ scale: [1, 1.08, 0.97, 1.05, 1], rotate: [0, 8, -6, 4, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute w-[600px] h-[600px] rounded-full pointer-events-none opacity-50"
        style={{
          background: `radial-gradient(ellipse at 45% 45%, hsl(258 45% 88%), hsl(355 70% 91%), transparent 70%)`,
        }}
      />

      {/* Morphing blob — color shifts with each message */}
      <AnimatePresence mode="wait">
        <motion.div
          key={msgIndex}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{
            opacity: 1,
            scale: [0.95, 1.08, 0.98, 1.06, 1],
            borderRadius: [
              "60% 40% 30% 70% / 60% 30% 70% 40%",
              "40% 60% 70% 30% / 50% 60% 40% 50%",
              "55% 45% 50% 50% / 40% 60% 50% 60%",
              "30% 70% 40% 60% / 70% 30% 60% 40%",
              "60% 40% 30% 70% / 60% 30% 70% 40%",
            ],
          }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute w-64 h-64 pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 40% 40%, ${msg.blobColor}, transparent 70%)`,
            opacity: 0.6,
          }}
        />
      </AnimatePresence>

      {/* Petal/leaf floating particles */}
      {[0, 1, 2, 3, 4].map((i) => (
        <motion.div
          key={i}
          animate={{
            y: [0, -30, 0, -20, 0],
            x: [0, (i % 2 === 0 ? 1 : -1) * 15, 0],
            rotate: [0, 25, -15, 10, 0],
            opacity: [0.15, 0.35, 0.15],
          }}
          transition={{
            duration: 5 + i * 1.3,
            repeat: Infinity,
            delay: i * 0.7,
            ease: "easeInOut",
          }}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 8 - i * 0.8,
            height: 8 - i * 0.8,
            background: msg.blobColor,
            top: `${30 + i * 8}%`,
            left: `${20 + i * 12}%`,
          }}
        />
      ))}

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center text-center px-8 gap-7">

        {/* Animated logo mark */}
        <motion.div
          animate={{ scale: [0.94, 1.06, 0.94] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg relative"
          style={{ background: `linear-gradient(135deg, hsl(258 45% 88%), hsl(355 70% 91%))` }}
        >
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-2xl border border-white/60"
            style={{ borderRadius: "inherit" }}
          />
          <span className="font-display font-medium text-xl text-primary/80">U</span>
        </motion.div>

        {/* Cycling message */}
        <div className="h-9 relative w-full flex justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={msgIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45 }}
              className="font-display text-lg sm:text-xl md:text-2xl text-foreground/70 font-medium absolute whitespace-nowrap"
            >
              {msg.text}
            </motion.p>
          </AnimatePresence>
        </div>

        {/* Pulsing line loader */}
        <div className="flex items-center gap-1.5">
          {MESSAGES.map((_, i) => (
            <motion.div
              key={i}
              animate={{
                width: i === msgIndex ? 24 : 6,
                opacity: i === msgIndex ? 1 : 0.3,
              }}
              transition={{ duration: 0.4 }}
              className="h-1.5 rounded-full bg-primary"
            />
          ))}
        </div>

        <p className="text-sm text-muted-foreground/60 tracking-wide">
          Unfolding your entry…
        </p>
      </div>
    </div>
  );
}
